import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Autocomplete, Chip, IconButton, Tooltip } from 'components/mui';
import { TuneOutlinedIcon, ClearIcon } from 'components/icons';
import { CommentFilter } from '../../hooks/useCommentsDashboard';
import searchCustomers, { CustomerOption } from 'api/cases/searchCustomers';
import searchConsultants, { ConsultantOption } from 'api/cases/searchConsultants';
import CommentFilterChips from './CommentFilterChips';

interface CommentFilterBarProps {
	filters: CommentFilter;
	onFilterChange: (field: keyof CommentFilter, value: unknown) => void;
	onApplyFilters: () => void;
	onClearFilters: () => void;
}

function CommentFilterBar({ filters, onFilterChange, onApplyFilters, onClearFilters }: CommentFilterBarProps) {
	const [expanded, setExpanded] = useState(false);
	const [customerOptions, setCustomerOptions] = useState<CustomerOption[]>([]);
	const [consultantOptions, setConsultantOptions] = useState<ConsultantOption[]>([]);
	const [selectedCustomer, setSelectedCustomer] = useState<CustomerOption | null>(null);
	const [selectedConsultant, setSelectedConsultant] = useState<ConsultantOption | null>(null);

	useEffect(() => {
		if (filters.CustomerId === null) setSelectedCustomer(null);
		if (filters.ConsultantId === null) setSelectedConsultant(null);
	}, [filters.CustomerId, filters.ConsultantId]);

	const hasActiveFilters = filters.CustomerId !== null || filters.ConsultantId !== null || filters.Date !== '';
	const activeCount = [filters.CustomerId !== null, filters.ConsultantId !== null, filters.Date !== ''].filter(Boolean).length;

	const customerOpts = selectedCustomer && !customerOptions.some((c) => c.Id === selectedCustomer.Id) ? [selectedCustomer, ...customerOptions] : customerOptions;
	const consultantOpts = selectedConsultant && !consultantOptions.some((c) => c.Id === selectedConsultant.Id) ? [selectedConsultant, ...consultantOptions] : consultantOptions;

	const handleClearSingle = (field: keyof CommentFilter) => {
		if (field === 'CustomerId') { setSelectedCustomer(null); onFilterChange('CustomerId', null); }
		else if (field === 'ConsultantId') { setSelectedConsultant(null); onFilterChange('ConsultantId', null); }
		else if (field === 'Date') { onFilterChange('Date', ''); }
	};

	const inputSx = { '& .MuiOutlinedInput-root': { bgcolor: 'background.paper' } };

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
				<Tooltip title={expanded ? 'Hide filters' : 'Show filters'}>
					<Button
						size="small" variant={hasActiveFilters ? 'contained' : 'outlined'}
						startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
						onClick={() => setExpanded((p) => !p)}
						sx={{ textTransform: 'none', fontWeight: 600, minWidth: 'auto', px: 1.5, py: 0.5 }}
					>
						Filters
						{activeCount > 0 && (
							<Chip label={activeCount} size="small" sx={{
								ml: 0.75, height: 18, fontSize: '0.7rem', fontWeight: 700,
								bgcolor: hasActiveFilters ? 'primary.contrastText' : 'primary.main',
								color: hasActiveFilters ? 'primary.main' : 'primary.contrastText',
							}} />
						)}
					</Button>
				</Tooltip>
				<CommentFilterChips
					selectedCustomer={selectedCustomer} selectedConsultant={selectedConsultant}
					dateValue={filters.Date} onClear={handleClearSingle}
				/>
				{hasActiveFilters && (
					<Tooltip title="Clear all filters">
						<IconButton size="small" onClick={onClearFilters} sx={{ ml: 'auto' }}>
							<ClearIcon sx={{ fontSize: 16 }} />
						</IconButton>
					</Tooltip>
				)}
			</Box>

			{expanded && (
				<Box sx={{
					display: 'grid',
					gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr auto' },
					gap: 1.5, mt: 1.5, alignItems: 'start',
				}}>
					<Autocomplete size="small" options={customerOpts} getOptionLabel={(o) => o.Name} value={selectedCustomer}
						onChange={(_e, v) => { setSelectedCustomer(v); onFilterChange('CustomerId', v?.Id || null); }}
						onInputChange={(_e, v) => { searchCustomers(v).then(setCustomerOptions).catch(() => {}); }}
						onOpen={() => { searchCustomers('').then(setCustomerOptions).catch(() => {}); }}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						renderInput={(p) => <TextField {...p} label="Customer" />} sx={inputSx}
					/>
					<Autocomplete size="small" options={consultantOpts} getOptionLabel={(o) => o.FullName} value={selectedConsultant}
						onChange={(_e, v) => { setSelectedConsultant(v); onFilterChange('ConsultantId', v?.Id || null); }}
						onInputChange={(_e, v) => { searchConsultants(v).then(setConsultantOptions).catch(() => {}); }}
						onOpen={() => { searchConsultants('').then(setConsultantOptions).catch(() => {}); }}
						isOptionEqualToValue={(o, v) => o.Id === v.Id}
						renderInput={(p) => <TextField {...p} label="Consultant" />} sx={inputSx}
					/>
					<TextField size="small" type="date" label="Date" value={filters.Date}
						onChange={(e) => onFilterChange('Date', e.target.value)} InputLabelProps={{ shrink: true }} sx={inputSx}
					/>
					<Button variant="contained" size="small" onClick={onApplyFilters}
						sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', md: 'center' }, mt: { xs: 0, md: 0.25 } }}
					>
						Apply
					</Button>
				</Box>
			)}
		</Box>
	);
}

export default CommentFilterBar;
